import { createReducer, on } from "@ngrx/store";
import * as ProductsActions from "./actions";
import { ProductsStateInterface } from "src/app/products/models/products-state-interface";
import { IProduct } from "src/app/products/models/product";

export const initialState: ProductsStateInterface = {
    isLoading: false,
    isLoadingImport: false,
    products: [],
    total: 0,
    error: null,
};

export const productsReducers = createReducer(
    initialState, 

    // get Products
    on(ProductsActions.getProducts, (state) => ({
        ...state, 
        isLoading: true
    })),
    on(ProductsActions.getProductsSuccess, (state, action) => ({
        ...state, 
        isLoading: false, 
        products: action.products.slice().sort((a, b) => a.name.localeCompare(b.name)),
        total: action.total
    })),
    on(ProductsActions.getProductsFailure, (state, action) => ({
        ...state, 
        isLoading: false,
        error: action.error
    })),

    // create Product
    on(ProductsActions.createProduct, (state) => ({
        ...state, 
        isLoading: true
    })),
    on(ProductsActions.createProductSuccess, (state, action) => {
        // Ensure state.products is an array (avoid potential undefined issues)
        const existingProducts = state.products ?? [];
    
        // Add the new product
        const updatedProducts = [...existingProducts, action.product];
    
        // Sort products by serial number (handling null values)
        const sortedProducts = updatedProducts.sort((a, b) => {
            const serialA = a.name || '';
            const serialB = b.name || '';
            return serialA.localeCompare(serialB);
        });
    
        return {
            ...state,
            isLoading: false,
            products: sortedProducts,
            total: state.total + 1
        };
    }),
    
    on(ProductsActions.createProductFailure, (state, action) => ({
        ...state, 
        isLoading: false,
        error: action.error
    })),

    // import Products
    on(ProductsActions.importProducts, (state) => ({
        ...state, 
        isLoadingImport: true
    })),
    on(ProductsActions.importProductsSuccess, (state, action) => {
        // Create a map of existing products by their internalNumber
        const existingProductsMap = new Map(state.products.map(part => [part.internalNumber, part]));
    
        // Create a new map to hold the updated list of products
        const updatedProductsMap = new Map<string, IProduct>();
    
        // Add existing product to the new map
        state.products.forEach(part => updatedProductsMap.set(part.internalNumber, part));
    
        // Iterate through the new products
        action.products.forEach(part => {
          if (updatedProductsMap.has(part.internalNumber)) {
            // Update the existing products with new data
            updatedProductsMap.set(part.internalNumber, {
              ...updatedProductsMap.get(part.internalNumber)!,
              ...part,
            });
          } else {
            // Add the new product to the map
            updatedProductsMap.set(part.internalNumber, part);
          }
        });
    
        // Convert the map to an array and sort it by name, handling null values
        const sortedProducts = Array.from(updatedProductsMap.values()).sort((a, b) => {
          const nameA = a.name || '';
          const nameB = b.name || '';
          return nameA.localeCompare(nameB);
        });
    
        return {
          ...state,
          isLoadingImport: false,
          products: sortedProducts,
        };
      }),
    on(ProductsActions.importProductsFailure, (state, action) => ({
        ...state, 
        isLoadingImport: false,
        error: action.error
    })),    

    // update Product
    on(ProductsActions.updateProduct, (state) => ({
        ...state, 
        isLoading: true
    })),
    on(ProductsActions.updateProductSuccess, (state, action) => {
        const updatedIndex = state.products.findIndex(product => product.id === action.product.id);
        
        if (updatedIndex === -1) {
            return state;
        }

        const updated = [...state.products];
        updated[updatedIndex] = action.product;

        return {
            ...state,
            isLoading: false,
            products: updated.sort((a, b) => a.name.localeCompare(b.name))
        };
    }),
    on(ProductsActions.updateProductFailure, (state, action) => ({
        ...state, 
        isLoading: false,
        error: action.error
    })),

    // delete Product
    on(ProductsActions.deleteProduct, (state) => ({
        ...state, 
        isLoading: true
    })),
    on(ProductsActions.deleteProductSuccess, (state,action) => ({
        ...state, 
        isLoading: false,
        products: state.products.filter(part => part.id !== action.id),
        total: state.total - 1
      })),
    on(ProductsActions.deleteProductFailure, (state, action) => ({
        ...state, 
        isLoading: false,
        error: action.error
    })),
);